import type {
  CandidateLocationFilterValue,
  SearchFilterValue,
  StructuredSearchFilterDefinition,
  StructuredSearchFilterSelection,
} from "./structured-search.data";
import { isFiniteNumber } from "@quake/isomorphic";



function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function normalizeDateValue(value: unknown) {
  if (!isNonEmptyString(value)) return undefined;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;

  return parsed.toISOString();
}

function normalizeValues(values: string[]) {
  const seen = new Set<string>();
  const normalized: string[] = [];

  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(trimmed);
  }

  return normalized;
}

export function normalizeStructuredTextListValue(value: string) {
  return value.trim().replace(/\s+/g, " ");
}

export function coerceStructuredSearchFilters(input: unknown): SearchFilterValue {
  if (!input || typeof input !== "object" || Array.isArray(input)) return {};

  const next: SearchFilterValue = {};

  for (const [key, rawValue] of Object.entries(input as Record<string, unknown>)) {
    if (Array.isArray(rawValue)) {
      const values = normalizeValues(rawValue.filter((value): value is string => typeof value === "string"));
      if (values.length > 0) {
        next[key] = { values };
      }
      continue;
    }

    if (!rawValue || typeof rawValue !== "object") continue;

    const candidate = rawValue as Record<string, unknown>;
    const selection: StructuredSearchFilterSelection = {};

    if (Array.isArray(candidate.values)) {
      const values = normalizeValues(candidate.values.filter((value): value is string => typeof value === "string"));
      if (values.length > 0) {
        selection.values = values;
      }
    }

    if (isFiniteNumber(candidate.min)) {
      selection.min = candidate.min;
    }

    if (isFiniteNumber(candidate.max)) {
      selection.max = candidate.max;
    }

    const from = normalizeDateValue(candidate.from);
    if (from) {
      selection.from = from;
    }

    const to = normalizeDateValue(candidate.to);
    if (to) {
      selection.to = to;
    }

    if (
      (selection.values?.length ?? 0) > 0 ||
      selection.min !== undefined ||
      selection.max !== undefined ||
      selection.from !== undefined ||
      selection.to !== undefined
    ) {
      next[key] = selection;
    }
  }

  return next;
}

export function getStructuredFilterValues(selection?: StructuredSearchFilterSelection) {
  return normalizeValues(selection?.values ?? []);
}

export function serializeCandidateLocationFilterValue(value: CandidateLocationFilterValue) {
  return JSON.stringify({
    provider: "geoapify",
    placeId: value.placeId,
    label: value.label,
    mode: value.mode,
    latitude: value.latitude,
    longitude: value.longitude,
    ...(isFiniteNumber(value.radiusMiles) ? { radiusMiles: value.radiusMiles } : {}),
    ...(value.countryCode ? { countryCode: value.countryCode } : {}),
    ...(value.resultType ? { resultType: value.resultType } : {}),
  });
}

export function parseCandidateLocationFilterValue(value: string): CandidateLocationFilterValue | null {
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (
      parsed.provider !== "geoapify" ||
      typeof parsed.placeId !== "string" ||
      typeof parsed.label !== "string" ||
      (parsed.mode !== "in" && parsed.mode !== "near") ||
      !isFiniteNumber(parsed.latitude) ||
      !isFiniteNumber(parsed.longitude)
    ) {
      return null;
    }

    return {
      provider: "geoapify",
      placeId: parsed.placeId,
      label: parsed.label,
      mode: parsed.mode,
      latitude: parsed.latitude,
      longitude: parsed.longitude,
      radiusMiles: isFiniteNumber(parsed.radiusMiles) ? parsed.radiusMiles : undefined,
      countryCode: isNonEmptyString(parsed.countryCode) ? parsed.countryCode : undefined,
      resultType: isNonEmptyString(parsed.resultType) ? parsed.resultType : undefined,
    };
  } catch {
    return null;
  }
}

export function getCandidateLocationFilterValues(selection?: StructuredSearchFilterSelection) {
  return getStructuredFilterValues(selection).map((value) => ({
    raw: value,
    location: parseCandidateLocationFilterValue(value),
    label: parseCandidateLocationFilterValue(value)?.label ?? value,
  }));
}

export function setCandidateLocationFilterValues(
  filters: SearchFilterValue,
  filterId: string,
  values: Array<CandidateLocationFilterValue | string>,
) {
  return setStructuredFilterValues(
    filters,
    filterId,
    values.map((value) => (typeof value === "string" ? value : serializeCandidateLocationFilterValue(value))),
  );
}

export function getStructuredDateRange(selection?: StructuredSearchFilterSelection) {
  return {
    from: normalizeDateValue(selection?.from),
    to: normalizeDateValue(selection?.to),
  };
}

export function buildStructuredFilterSectionState(filterIds: string[], current: Record<string, boolean> = {}) {
  return Object.fromEntries(filterIds.map((filterId) => [filterId, current[filterId] ?? false]));
}

export function getStructuredFilterHeaderMeta(
  filter: StructuredSearchFilterDefinition,
  selection?: StructuredSearchFilterSelection,
  args: { availableOptionsCount?: number | null } = {},
) {
  if (filter.type === "multi-select") {
    return {
      isActive: getStructuredFilterValues(selection).length > 0,
      availableOptionsCount: args.availableOptionsCount ?? filter.options?.length ?? null,
      selectedOptionsCount: getStructuredFilterValues(selection).length,
    };
  }

  return {
    isActive: isStructuredFilterSelectionActive(selection),
    availableOptionsCount: null,
    selectedOptionsCount: 0,
  };
}

export function isStructuredRangeInvalid(
  selection?: StructuredSearchFilterSelection,
  options: { allowNegative?: boolean } = {},
) {
  const min = selection?.min;
  const max = selection?.max;

  if (!options.allowNegative) {
    if (isFiniteNumber(min) && min < 0) return true;
    if (isFiniteNumber(max) && max < 0) return true;
  }

  if (isFiniteNumber(min) && isFiniteNumber(max) && min > max) return true;

  return false;
}

export function isStructuredDateRangeInvalid(selection?: StructuredSearchFilterSelection) {
  const { from, to } = getStructuredDateRange(selection);

  if (!from || !to) return false;

  return new Date(from).getTime() > new Date(to).getTime();
}

export function isStructuredFilterSelectionActive(
  selection?: StructuredSearchFilterSelection,
  options: { allowNegative?: boolean; ignoreInvalidity?: boolean } = {},
) {
  const valuesCount = getStructuredFilterValues(selection).length;
  if (valuesCount > 0) return true;

  const hasRange = isFiniteNumber(selection?.min) || isFiniteNumber(selection?.max);
  if (hasRange) {
    if (options.ignoreInvalidity) return true;
    return !isStructuredRangeInvalid(selection, options);
  }

  const { from, to } = getStructuredDateRange(selection);
  const hasDateRange = !!from || !!to;
  if (!hasDateRange) return false;

  return !isStructuredDateRangeInvalid(selection);
}

export function getActiveStructuredFilterCount(
  filters: SearchFilterValue,
  definitions: StructuredSearchFilterDefinition[] = [],
) {
  return Object.entries(filters).reduce((sum, [filterId, selection]) => {
    const valuesCount = getStructuredFilterValues(selection).length;
    if (valuesCount > 0) return sum + valuesCount;

    const definition = definitions.find((d) => d.id === filterId);
    const allowNegative = definition?.type === "number-range" ? definition.allowNegative : false;

    if (isStructuredFilterSelectionActive(selection, { allowNegative })) return sum + 1;
    return sum;
  }, 0);
}

export function sanitizeStructuredFilters(
  filters: SearchFilterValue,
  options: { keepInvalidRanges?: boolean } = {},
): SearchFilterValue {
  const next: SearchFilterValue = {};

  for (const [key, selection] of Object.entries(filters)) {
    const values = getStructuredFilterValues(selection);
    if (values.length > 0) {
      next[key] = { values };
      continue;
    }

    if (!isStructuredFilterSelectionActive(selection, { ignoreInvalidity: options.keepInvalidRanges })) continue;

    const rangeSelection: StructuredSearchFilterSelection = {};
    if (isFiniteNumber(selection.min)) {
      rangeSelection.min = selection.min;
    }
    if (isFiniteNumber(selection.max)) {
      rangeSelection.max = selection.max;
    }

    if (rangeSelection.min !== undefined || rangeSelection.max !== undefined) {
      next[key] = rangeSelection;
      continue;
    }

    const { from, to } = getStructuredDateRange(selection);
    if (!from && !to) continue;
    if (isStructuredDateRangeInvalid(selection)) continue;

    const dateSelection: StructuredSearchFilterSelection = {};
    if (from) {
      dateSelection.from = from;
    }
    if (to) {
      dateSelection.to = to;
    }

    if (dateSelection.from !== undefined || dateSelection.to !== undefined) {
      next[key] = dateSelection;
    }
  }

  return next;
}

export function normalizeFiltersForComparison(filters: SearchFilterValue): SearchFilterValue {
  const sanitized = sanitizeStructuredFilters(filters, { keepInvalidRanges: true });
  const next: SearchFilterValue = {};
  const keys = Object.keys(sanitized).sort((left, right) => left.localeCompare(right));

  for (const key of keys) {
    const selection = sanitized[key];
    if (!selection) continue;

    const normalizedSelection: StructuredSearchFilterSelection = {};

    if (Array.isArray(selection.values) && selection.values.length > 0) {
      normalizedSelection.values = [...selection.values].sort((left, right) => left.localeCompare(right));
    }

    if (isFiniteNumber(selection.min)) {
      normalizedSelection.min = selection.min;
    }

    if (isFiniteNumber(selection.max)) {
      normalizedSelection.max = selection.max;
    }

    const { from, to } = getStructuredDateRange(selection);
    if (from) {
      normalizedSelection.from = from;
    }

    if (to) {
      normalizedSelection.to = to;
    }

    if (Object.keys(normalizedSelection).length > 0) {
      next[key] = normalizedSelection;
    }
  }

  return next;
}

export function setStructuredFilterValues(filters: SearchFilterValue, filterId: string, values: string[]) {
  const normalizedValues = normalizeValues(values);
  if (normalizedValues.length === 0) {
    const next = { ...filters };
    delete next[filterId];
    return next;
  }

  return {
    ...filters,
    [filterId]: { values: normalizedValues },
  };
}

export function addStructuredTextListValue(filters: SearchFilterValue, filterId: string, rawValue: string) {
  const normalizedValue = normalizeStructuredTextListValue(rawValue);
  if (!normalizedValue) return filters;
  return setStructuredFilterValues(filters, filterId, [
    ...getStructuredFilterValues(filters[filterId]),
    normalizedValue,
  ]);
}

export function removeStructuredTextListValue(filters: SearchFilterValue, filterId: string, value: string) {
  return setStructuredFilterValues(
    filters,
    filterId,
    getStructuredFilterValues(filters[filterId]).filter((item) => item !== value),
  );
}

export function updateStructuredRangeFilter(
  filters: SearchFilterValue,
  filterId: string,
  patch: { min?: number; max?: number },
) {
  const current = filters[filterId] ?? {};
  const nextSelection: StructuredSearchFilterSelection = {
    ...current,
    ...patch,
  };

  if (!isFiniteNumber(nextSelection.min)) {
    delete nextSelection.min;
  }

  if (!isFiniteNumber(nextSelection.max)) {
    delete nextSelection.max;
  }

  if ((nextSelection.values?.length ?? 0) === 0) {
    delete nextSelection.values;
  }

  if (nextSelection.min === undefined && nextSelection.max === undefined && !nextSelection.values) {
    const next = { ...filters };
    delete next[filterId];
    return next;
  }

  return {
    ...filters,
    [filterId]: nextSelection,
  };
}

export function updateStructuredDateRangeFilter(
  filters: SearchFilterValue,
  filterId: string,
  patch: { from?: string; to?: string },
) {
  const current = filters[filterId] ?? {};
  const nextSelection: StructuredSearchFilterSelection = {
    ...current,
    ...patch,
  };

  const from = normalizeDateValue(nextSelection.from);
  const to = normalizeDateValue(nextSelection.to);

  if (from) {
    nextSelection.from = from;
  } else {
    delete nextSelection.from;
  }

  if (to) {
    nextSelection.to = to;
  } else {
    delete nextSelection.to;
  }

  if (!isFiniteNumber(nextSelection.min)) {
    delete nextSelection.min;
  }

  if (!isFiniteNumber(nextSelection.max)) {
    delete nextSelection.max;
  }

  if ((nextSelection.values?.length ?? 0) === 0) {
    delete nextSelection.values;
  }

  if (
    nextSelection.min === undefined &&
    nextSelection.max === undefined &&
    nextSelection.from === undefined &&
    nextSelection.to === undefined &&
    !nextSelection.values
  ) {
    const next = { ...filters };
    delete next[filterId];
    return next;
  }

  return {
    ...filters,
    [filterId]: nextSelection,
  };
}
